<template>
  <div
    class="flex flex-col-reverse items-center justify-center gap-x-6 font-montserrat text-xs font-normal lg:flex-row lg:gap-x-4 lg:text-sm lg:font-semibold lg:text-s-700 xl:gap-x-6"
  >
    <div class="flex w-full justify-center gap-x-3 lg:justify-start lg:gap-x-6">
      <div
        class="flex min-w-fit rounded-3xl border border-s-900 px-5 py-1.5 hover:cursor-pointer sm:px-6 lg:flex-grow lg:border-0 lg:p-0"
        :class="[value === 'All Time' ? 'underline' : '']"
        @click="updateDateRange('All Time')"
      >
        All Time
      </div>
      <div
        class="flex min-w-fit rounded-3xl border border-s-900 px-5 py-1.5 hover:cursor-pointer sm:px-6 lg:flex-grow lg:border-0 lg:p-0"
        :class="[value === 'Last Calendar Year' ? 'underline' : '']"
        @click="updateDateRange('Last Calendar Year')"
      >
        1 Year
      </div>
      <div
        class="flex min-w-fit rounded-3xl border border-s-900 px-5 py-1.5 hover:cursor-pointer sm:px-6 lg:flex-grow lg:border-0 lg:p-0"
        :class="[value === 'YTD' ? 'underline' : '']"
        @click="updateDateRange('YTD')"
      >
        YTD
      </div>
    </div>

    <div
      class="custom-calendar z-20 mb-5 flex h-full w-full justify-end gap-x-3 border-b border-s-300 pb-8 lg:mb-0 lg:gap-x-1 lg:border-b-0 lg:pb-0 xl:gap-x-6"
    >
      <v-date-picker
        ref="picker"
        v-model="startDateComputed"
        :first-day-of-week="2"
        :min-date="minDate"
        :max-date="maxDate"
        :locale="localeLanguage"
        :select-attribute="attribute"
        :popover="{ showDelay: 500, visibility: 'focus' }"
        @dayclick="onDayClick"
      >
        <template #default="{ inputValue, inputEvents }">
          <div>
            <div class="relative flex w-full flex-wrap items-stretch">
              <label
                class="mb-2 text-sm font-semibold text-s-900 lg:hidden"
                for="input-date-start"
              >
                Start date
              </label>
              <span
                class="text-gray-400 absolute z-10 flex h-full w-8 items-center justify-center rounded bg-transparent pb-1 pl-3 pt-8 text-center text-base font-normal leading-snug lg:pb-3 lg:pt-3"
              >
                <DatesChartIcon class="h-4 w-4" />
              </span>
              <input
                id="input-date-start"
                :value="inputValue"
                class="relative w-full rounded border border-s-300 p-2 pl-10 text-sm focus:outline-none lg:w-[8.5rem]"
                data-testid="chart-start-date-picker"
                :disabled="disabled"
                v-on="inputEvents"
              />
            </div>
          </div>
        </template>
      </v-date-picker>
      <v-date-picker
        ref="picker"
        v-model="endDateComputed"
        :first-day-of-week="2"
        :min-date="startDateComputed"
        :max-date="maxDate"
        :locale="localeLanguage"
        :select-attribute="attribute"
        :popover="{ showDelay: 500, visibility: 'focus' }"
        @dayclick="onDayClick"
      >
        <template #default="{ inputValue, inputEvents }">
          <div>
            <div class="relative flex w-full flex-wrap items-stretch">
              <label
                class="mb-2 text-sm font-semibold text-s-900 lg:hidden"
                for="input-date-end"
              >
                End date
              </label>
              <span
                class="text-gray-400 absolute z-10 flex h-full w-8 items-center justify-center rounded bg-transparent pb-1 pl-3 pt-8 text-center text-base font-normal leading-snug lg:pb-3 lg:pt-3"
              >
                <DatesChartIcon class="h-4 w-4" />
              </span>
              <input
                id="input-date-end"
                :value="inputValue"
                class="relative w-full rounded border border-s-300 p-2 pl-10 text-sm focus:outline-none lg:w-[8.5rem]"
                data-testid="chart-end-date-picker"
                :disabled="disabled"
                v-on="inputEvents"
              />
            </div>
          </div>
        </template>
      </v-date-picker>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import { sub } from 'date-fns'
import { getLastCalendarYear, getYTD } from '~/utils/date'

const DatesChartIcon = defineAsyncComponent(
  () => import('~/assets/icons/mobile/charts/Dates.svg'),
)

const props = withDefaults(
  defineProps<{
    minDate: Date
    maxDate?: Date
    startDate?: string
    endDate?: string
    defaultValue?: string
    loading?: boolean
    disabled?: boolean
  }>(),
  {
    maxDate: () => new Date(),
    startDate: () => sub(new Date(), { years: 1 }).toISOString(),
    endDate: () => new Date().toISOString(),
    defaultValue: 'Last Calendar Year',
    loading: false,
    disabled: false,
  },
)

const { $theme } = useNuxtApp()
const emit = defineEmits(['date-range-updated'])
const value = ref(props.defaultValue)
const range = ref({
  start: new Date(props.startDate),
  end: new Date(props.endDate),
})
const attribute = ref({
  highlight: {
    start: {
      style: {
        backgroundColor: $theme.colors.p['500'],
      },
      contentStyle: {
        color: $theme.colors.s['900'],
      },
    },
  },
})
const localeLanguage = ref(navigator.language)

const startDateComputed = computed({
  get: () => range.value.start,
  set: (value?: Date) => {
    if (!value) return
    range.value.start = value
  },
})

const endDateComputed = computed({
  get: () => range.value.end,
  set: (value?: Date) => {
    if (!value) return
    range.value.end = value
  },
})

const onDayClick = (event: Event) => {
  ;(event.target as HTMLElement)?.blur()
  updateDateRange('Custom Date Range')
}

const updateDateRange = (option: string) => {
  if (props.disabled) return
  value.value = option

  const ranges = {
    'Last Calendar Year': getLastCalendarYear(new Date()),
    'All Time': { start: props.minDate, end: props.maxDate },
    YTD: getYTD(new Date()),
  }

  const newRange = ranges[option]

  if (!newRange) {
    emit('date-range-updated', { range: range.value, option })
    return
  }

  range.value = newRange
  emit('date-range-updated', { range: range.value, option })
}
</script>
